import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockCheckbox from '../components/GuideMockCheckbox';
import GuideMockContainer from '../components/GuideMockContainer';
import GuideMockRadioGroup from '../components/GuideMockRadioGroup';
import GuideMockSelect from '../components/GuideMockSelect';
import GuideMockTextField from '../components/GuideMockTextField';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideMemberRegisterExternal: React.FC = () => {
  return (
    <GuidePageLayout
      title="社外要員を登録する"
      description="パートナー企業に所属する社外要員を登録します。"
      breadcrumbs={[{ label: '要員管理', path: '/guide/member/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者 / 管理部門 / 課長'],
            ['ログイン状態', 'ログイン済みであること'],
            ['事前準備', '所属するパートナーが登録済みであること'],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: '要員管理画面を開く' },
            {
              label: '「要員新規作成」ボタンを押下して登録フォームを開く',
            },
            {
              label: '雇用形態で「社外」を選択し、要員情報を入力する',
              description:
                'フォームタイトルは「要員登録」です。タイトル左の戻る矢印をクリックすると、登録を破棄して一覧に戻ります。',
              content: (
                <GuideMockContainer label="登録フォーム（社外）">
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        要員登録
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <GuideMockTextField label="要員名" required />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="要員名（カナ）" required />
                      </Grid>
                      <Grid size={12}>
                        <GuideMockRadioGroup
                          label="雇用形態"
                          options={['社員', '社外']}
                          required
                          value="社外"
                        />
                      </Grid>
                      <Grid size={12}>
                        <GuideMockRadioGroup
                          label="性別"
                          options={['男性', '女性', 'その他']}
                          required
                        />
                      </Grid>
                      <Grid size={12}>
                        <GuideMockCheckbox label="個人事業主" />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockSelect
                          label="パートナー"
                          options={['サンプル株式会社', 'テスト企業']}
                          required
                        />
                      </Grid>
                      <Grid size={12}>
                        <GuideMockRadioGroup
                          label="契約形態"
                          options={['派遣', 'SES', '業務委託']}
                          required
                        />
                      </Grid>
                      <Grid size={12}>
                        <GuideMockTextField label="備考（6行表示）" />
                      </Grid>
                    </Grid>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「作成」ボタンを押下する',
              content: (
                <GuideMockContainer label="フッターボタン">
                  <GuideMockButton label="作成" startIcon={<PersonAddIcon />} />
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="入力項目">
        <GuideTable
          headers={['項目名', '必須', '形式', '備考']}
          rows={[
            ['要員名', '必須', 'テキスト（最大300桁）', ''],
            ['要員名（カナ）', '必須', 'カタカナテキスト（最大300桁）', ''],
            ['雇用形態', '必須', 'ラジオボタン', '「社外」を選択'],
            ['性別', '必須', 'ラジオボタン', 'マスタ区分から選択'],
            ['パートナー', '必須', 'パートナー選択', '登録済みパートナーから選択'],
            ['契約形態', '必須', 'ラジオボタン', '派遣 / SES / 業務委託'],
            ['個人事業主', '任意', 'チェックボックス', '個人事業主の場合にON'],
            ['備考', '任意', 'テキストエリア（最大10000桁）', '6行表示'],
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', '動作']}
          rows={[
            ['← 戻る矢印（タイトル左）', '登録を破棄して一覧画面に戻る'],
            ['作成ボタン', '入力内容で要員を登録する'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox>要員IDは自動的に発行されます。</GuideInfoBox>
          <GuideInfoBox>
            所属パートナーは事前に登録されている必要があります（パートナー管理画面で登録）。
          </GuideInfoBox>
          <GuideInfoBox>個人事業主の場合、パートナーと要員は1:1の関係となります。</GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideMemberRegisterExternal;
